/* eslint-disable */

import { Loader } from '../Loader';
import * as ObjectModule from './Object';
import { TrueType } from './enums/TrueType';
import * as String from './String';
import * as Type from './Type';
import * as URL from './URL';

/**
 * @description image preloader
 * @param urls {Array} array of image URLs
 * @param callback {Function} executed when all images are preloaded
 */
export const preloadImages = ( urls: string[], callback: Function ): void => {
	let loadedCounter = 0;
	const toBeLoadedNumber = urls.length;

	/**
	 * @description callback output param for loaded / failed images
	 * @type {{loaded: Array, failed: Array}}
	 */
	const output = {
		loaded: [],
		failed: []
	};

	const preloadImage = ( url, anImageLoadedCallback, failedImageLoadCallback ) => {
		const img = new Image();
		img.src = url;
		img.onload = anImageLoadedCallback;
		img.onerror = failedImageLoadCallback;
	};

	urls.forEach(
		( url ) => {
			preloadImage(
				url,
				/**
				 * @description onLoad callback
				 * @param event {Event}
				 */
				( event ) => {
					loadedCounter++;
					output.loaded.push( event.target.src );
					if ( loadedCounter === toBeLoadedNumber && callback !== undefined ) callback( output );
				},
				/**
				 * @description onError callback
				 * @param event {Event}
				 */
				( event ) => {
					loadedCounter++;
					output.failed.push( event.target.src );
					if ( loadedCounter === toBeLoadedNumber && callback !== undefined ) callback( output );
				}
			);
		}
	);
};

/**
 * @description Imports code (css, js)
 * @param url {String}
 * @param parentElement {HTMLElement} default document.head
 * @param async {Boolean}
 * @param prepend {Boolean}
 * @param onLoadComplete {Function}
 * @param onLoadFailure {Function}
 * @param attributes {Object}
 */
export const code = (
	url: string,
	parentElement: HTMLElement = document.head,
	async: boolean = false,
	prepend: boolean = false,
	onLoadComplete: Function = undefined,
	onLoadFailure: Function = undefined,
	attributes: { [ key: string ]: string | number } = undefined
): void => {
	// check if import already exists in DOM
	if ( alreadyPresent( url ) === true ) {
		if ( onLoadComplete !== undefined ) onLoadComplete( { target: url, url: url } ); // call a callback method + exit
		return;
	}

	// parse script extension
	const pattern = /\.([0-9a-z]+)(?:[\?#]|$)/i;
	const type = ( url ).match( pattern )[ 1 ];

	/**
	 * @type {HTMLElement}
	 */
	let scriptElement;  // script obj placeholder
	const referenceElement = null; // reference element to insertBefore...

	switch ( type ) {
		case 'css':
			scriptElement = document.createElement( 'link' );
			scriptElement.rel = 'stylesheet';
			scriptElement.type = 'text/css';
			scriptElement.media = 'screen';
			scriptElement.href = url;
			break;
		case 'js':
			scriptElement = document.createElement( 'script' );
			scriptElement.type = 'text/javascript';
			scriptElement.src = url;
			if ( async === true ) scriptElement.async = async;
			break;
	}

	// check if we have any attributes that must be set on script element
	if ( Type.isDefined( attributes ) && Type.isObject( attributes ) ) {
		Object.keys( attributes ).forEach(
			( key ) => {
				// set attribute on script element
				scriptElement.setAttribute( key, attributes[ key ] );
			}
		);
	}

	/**
	 * @description Handle script onLoad event
	 * @param event {Event}
	 */
	scriptElement.onload = ( event ) => {
		if ( Type.isFunction( onLoadComplete ) ) onLoadComplete( event );
	};

	/**
	 * @description Handle Script onError event
	 * @param event {Event}
	 */
	scriptElement.onerror = ( event ) => {
		if ( Type.isFunction( onLoadFailure ) ) {
			onLoadFailure( event );
		} else {
			// if failure handler was not set execute load complete handler (if it was set)
			if ( Type.isFunction( onLoadComplete ) ) onLoadComplete( event );
		}
	};

	prepend !== true ? parentElement.appendChild( scriptElement ) : parentElement.insertBefore( scriptElement, referenceElement );
};

/**
 * @description Removes js and css code imports
 * @param url {String} URL of "script" / "style" to remove
 * @param id {String} element id (in our case id = 'url' of the "script" or "link" tag)
 * @param callback {Function} callback function
 * @param param {Object} param that should be passed by callback function
 */
export const removeCode = ( url: string, id: string = undefined, callback: Function = undefined, param: any = undefined ): void => {
	// check if import exists
	if ( alreadyPresent( url ) === false ) {
		return;
	}

	// check if we have element id and remove it
	if ( id !== undefined ) {
		document.getElementById( id ).parentNode.removeChild( document.getElementById( id ) );
		if ( callback !== undefined ) callback( param );
		return;
	}

	const type = url.split( '.' )[ url.split( '.' ).length - 1 ]; // determine type of file to remove
	const targetElement = ( type === 'js' ) ? 'script' : ( type === 'css' ) ? 'link' : 'none'; //determine element type to create nodelist from
	const targetAttribute = ( type === 'js' ) ? 'src' : ( type === 'css' ) ? 'href' : 'none'; //determine corresponding attribute to test for
	const allSuspects = document.getElementsByTagName( targetElement );

	for ( let i = allSuspects.length; i >= 0; i-- ) { // search backwards within nodelist for matching elements to remove
		if ( allSuspects[ i ] && allSuspects[ i ].getAttribute( targetAttribute ) !== null && allSuspects[ i ].getAttribute( targetAttribute ).indexOf( url ) !== -1 ) {
			allSuspects[ i ].parentNode.removeChild( allSuspects[ i ] );  // remove element by calling parentNode.removeChild()
		}
	}

	if ( callback !== undefined ) callback( param );
};

/**
 * @description Check if "script" or "link" URL is already present (parses "script" extension (css, js etc.) from URL etc.)
 * @param {string} url
 * @param {string} namespace
 * @param {boolean} urlCheck - flag to check if URL exists
 * @return {boolean}
 */
export const alreadyPresent = ( url: string, namespace: string = undefined, urlCheck: boolean = true ): boolean => {
	const cleanURL = url.split( '?' )[ 0 ].replace( /^https?\:\/\//i, '' ); // url without params and protocol
	const params = url.indexOf( '?' ) > -1 ? url.split( '?' )[ 1 ] : '';

	// parse script extension
	const pattern = /\.([0-9a-z]+)(?:[\?#]|$)/i;
	let type = ( url ).match( pattern )[ 1 ];

	// normalize type to tag name
	if ( type === 'css' ) type = 'link';
	if ( type === 'js' ) type = 'script';

	// get tags
	const elements = document.getElementsByTagName( type );

	/**
	 * @description Namespace check
	 * @param {string} ns
	 * @return {boolean}
	 */
	const namespaceExists = ( ns: string ): boolean => {
		switch ( Type.isString( ns ) ) {
			case false: // namespace is not passed in
				return true;
			case true: // namespace string was passed in
				return !Type.isNull( ObjectModule.getDeepVal( window, ns ) );
		}
	}

	// loop through tags
	for ( let i = 0; i < elements.length; i++ ) {
		const srcURL = elements[ i ][ ( type === 'script' ? 'src' : 'href' ) ].split( '?' )[ 0 ].replace( /^https?\:\/\//i, '' );
		// filter by import type
		switch ( type ) {
			case 'link':
				switch ( urlCheck ) {
					case false: // URL check is off
						return false;
						break;
					case true: // URL check is on
						// check if import src stripped of params matches passed url stripped of params
						if ( srcURL === cleanURL ) return true;
						break;
				}
				break;
			case 'script':
				switch ( urlCheck ) {
					case false: // URL check is off
						return namespaceExists( namespace );
						break;
					case true: // URL check is on
						// script url was found
						if ( srcURL === cleanURL ) {
							return namespaceExists( namespace );
						}
						break;
				}
				break;
		}
	}
	return false;
};

/**
 * @description Promise-based JavaScript + CSS Loader
 * @type {Loader}
 */
export const loader = Loader;

/**
 * @description Returns URL by path filename patterns + domain (if set)
 * @param {string} tag
 * @param {string[]} pathPatterns
 * @param {string} domain
 * @return {string | undefined}
 */
export const getFileURL = ( tag: string = '', pathPatterns: string[] = [], domain: string = undefined ): string | undefined => {
	const tags = document.getElementsByTagName( tag );
	for ( let i = 0; i < tags.length; i++ ) {
		const element = tags[ i ] as HTMLScriptElement | HTMLLinkElement | any;
		// For link elements (CSS), use href; for script elements, use src
		const url = tag === 'link' ? element.href : element.src;
		if ( !url ) continue;
		const parsed = URL.parser( url );
		if ( String.hasSubstring( parsed.pathname, pathPatterns ) ) {
			if ( Type.isString( domain ) ) {
				if ( parsed.host.indexOf( domain ) > -1 ) return url;
			}
			return url;
		}
	}
	return undefined;
};

/**
 * @description Generalized XMLHttpRequest() request
 * @param url {String}
 * @param callbackObj {Object}
 * @param successCallback {Function}
 * @param failureCallback {Function}
 * @param timeout {Number} Timeout in milliseconds, default: 10000
 */
export const xhrLoad = ( url: string, callbackObj: any, successCallback: Function, failureCallback: Function, timeout: number = 10000 ): void => {
	let xhr = new XMLHttpRequest();
	xhr.open( 'GET', url, true );	// xhr.withCredentials = "true"; - asynchronous call

	const xhrTimer = setTimeout(
		() => {
			xhr.abort();
			xhr = null;
			const errStr = 'Service call timeout';
			failureCallback.call( callbackObj, errStr );

		},
		timeout
	);

	xhr.onreadystatechange = () => {

		if ( xhr.readyState === 4 ) {
			clearTimeout( xhrTimer );

			if ( xhr.status === 200 ) {
				successCallback.call( callbackObj, xhr );
			} else {
				const errStr = 'xhrLoad load status != 200 xhr.status = ' + xhr.status;
				failureCallback.call( callbackObj, errStr );
				xhr = null;
			}

		}

	};

	xhr.send( null );
};

/**
 * @description Web Worker setup
 * @see {@link https://github.com/zevero/worker-create}
 * @return {Object}
 * @example
 * ```typescript
 * // Usage examples for Worker.create:
 * // Use when you only need one worker!
 * var worker = utils.Worker().create( function( e ) {         // from function
 *     self.postMessage( 'Example post from Worker' ); // your code here
 * });
 * // or
 * var worker = utils.Worker().create( "self.postMessage('Example post from Worker');" ); // from string
 *
 * // Usage example for Worker.createURL:
 * // Use when you need multiple workers!
 * var worker_url = utils.Worker().createURL( function( e ) {  // from function
 *     self.postMessage( 'Example post from Worker' ); // your code here
 * });
 * // or
 * var worker_url = utils.Worker().createURL( "self.postMessage( 'Example post from Worker');" ); // from string
 *
 * // now reuse worker_url as often as you want.
 * var worker1 = new Worker( worker_url );
 * var worker2 = new Worker( worker_url );
 *
 * // setup handling of message received from worker
 * worker1.onmessage = function ( event ) {
 *     console.log( 'worker.onmessage', event );
 * };
 *
 * // pass message to worker... can be used to start worker execution based om message...
 * worker.postMessage( 'message to web worker...' );
 * ```
 */
export const WebWorker = (): { createURL: Function, create: Function } => {

	const createURL = ( func_or_string: Function | string | any ): string => {
		const str = ( typeof func_or_string === TrueType.FUNCTION ) ? func_or_string.toString() : func_or_string;
		const blob = new Blob( [ 'self.onmessage =' + str ], { type: 'text/javascript' } );
		return window.URL.createObjectURL( blob );
	};

	const create = ( func_or_string ): Worker => {
		return new Worker( createURL( func_or_string ) );
	};

	return {

		/**
		 * @description Creates URL with blob function
		 * @param func_or_string {Function|String}
		 * @return {*}
		 * @public
		 */
		createURL: createURL,

		/**
		 * @description Creates Web worker instance
		 * @param func_or_string {Function|String}
		 * @return {Worker}
		 * @public
		 */
		create: create

	};

};